import React, { useState, useEffect } from 'react';
import {
  Layout,
  Input,
  List,
  Card,
  Row,
  Col,
  Breadcrumb,
  Typography,
  Button,
  Space,
  message,
  Avatar,
  Spin,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { Radio } from 'antd';
import customerApi from '../services/customerApi';
import { Customer } from '../services/customers';
import { Business } from '../services/businesses';
import { customerAuthService } from '../services/customerAuth';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

interface CustomerFile {
  id: number;
  customer_id: number;
  business_id?: number | null;
  name: string;
  path: string;
  type: string;
  mime_type: string;
  size: number;
  folder: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  formatted_size?: string;
}

interface Folder {
  folder: string;
  customer_id: number;
  business_id?: number | null;
  customer_name: string;
  file_count?: number;
  year?: number;
}

export const CustomerFiles: React.FC = () => {
  const [entityType, setEntityType] = useState<'customer' | 'business'>('customer');
  const [currentCustomer] = useState<Customer | null>(() => customerAuthService.getCurrentCustomer());
  const [customers] = useState<Customer[]>(currentCustomer ? [currentCustomer] : []);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    currentCustomer || null
  );
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [files, setFiles] = useState<CustomerFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (entityType === 'business') {
      loadBusinesses();
    }
  }, [entityType]);

  useEffect(() => {
    setSelectedFolder(null);
    setFolders([]);
    setFiles([]);
    setSearchText('');
    if (entityType === 'customer') {
      setSelectedCustomer(currentCustomer || null);
      setSelectedBusiness(null);
    } else {
      setSelectedCustomer(null);
      setSelectedBusiness(null);
    }
  }, [entityType, currentCustomer]);

  useEffect(() => {
    if (entityType === 'customer' && selectedCustomer) {
      loadFolders(selectedCustomer.id, null);
      setSelectedFolder(null);
      setFiles([]);
    } else if (entityType === 'business' && selectedBusiness) {
      loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      setSelectedFolder(null);
      setFiles([]);
    } else {
      setFolders([]);
      setSelectedFolder(null);
      setFiles([]);
    }
  }, [selectedCustomer, selectedBusiness, entityType]);

  useEffect(() => {
    if (selectedFolder) {
      const businessId = selectedFolder.business_id ?? (entityType === 'business' && selectedBusiness ? selectedBusiness.id : null);
      loadFiles(selectedFolder.customer_id, selectedFolder.folder, businessId);
    } else {
      setFiles([]);
    }
  }, [selectedFolder, entityType, selectedBusiness]);

  const normalizeFolderPath = (value: string) =>
    value
      .replace(/\\/g, '/')
      .replace(/\/+/g, '/')
      .replace(/^\/|\/$/g, '')
      .trim();

  const getFolderLabel = (path: string) => {
    const parts = normalizeFolderPath(path).split('/').filter(Boolean);
    return parts[parts.length - 1] || path;
  };

  const isDirectChildFolder = (path: string, parentPath: string) => {
    const normalizedPath = normalizeFolderPath(path);
    const normalizedParent = normalizeFolderPath(parentPath);
    if (!normalizedParent) {
      return normalizedPath !== '' && !normalizedPath.includes('/');
    }
    if (!normalizedPath.startsWith(`${normalizedParent}/`)) {
      return false;
    }
    const remainder = normalizedPath.slice(normalizedParent.length + 1);
    return remainder !== '' && !remainder.includes('/');
  };

  const selectFolderByPath = (folderPath: string | null) => {
    if (!folderPath) {
      setSelectedFolder(null);
      return;
    }
    const match = folders.find((folder) => folder.folder === folderPath);
    if (match) {
      setSelectedFolder(match);
    }
  };

  const loadBusinesses = async () => {
    setBusinessesLoading(true);
    try {
      const response = await customerApi.get('/customer/businesses');
      setBusinesses(response.data.data || response.data || []);
    } catch (error: any) {
      message.error('Failed to load businesses');
    } finally {
      setBusinessesLoading(false);
    }
  };

  const loadFolders = async (customerId: number, businessId: number | null = null) => {
    setFoldersLoading(true);
    try {
      let url = `/customer/files/folders?customer_id=${customerId}`;
      if (businessId) {
        url += `&business_id=${businessId}`;
      }
      const response = await customerApi.get(url);
      setFolders(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load folders');
    } finally {
      setFoldersLoading(false);
    }
  };

  const loadFiles = async (customerId: number, folder: string, businessId: number | null = null) => {
    setLoading(true);
    try {
      let url = `/customer/files?customer_id=${customerId}&folder=${encodeURIComponent(folder)}`;
      if (businessId) {
        url += `&business_id=${businessId}`;
      }
      const response = await customerApi.get(url);
      setFiles(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleView = async (file: CustomerFile) => {
    try {
      // Use axios to fetch file with authentication token
      const response = await customerApi.get(`/customer/files/${file.id}/view`, {
        responseType: 'blob',
      });
      
      // Create blob URL and open in new window
      const blob = new Blob([response.data], { type: response.headers['content-type'] || file.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view file');
    }
  };

  const handleDownload = async (file: CustomerFile) => {
    try {
      // Use axios to fetch file with authentication token
      const response = await customerApi.get(`/customer/files/${file.id}/download`, {
        responseType: 'blob',
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] || file.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download file');
    }
  };

  const getFileIcon = (type: string) => {
    const iconStyle = { fontSize: 48, color: '#ff4d4f' };
    return <FileTextOutlined style={iconStyle} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const getCustomerInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const filteredCustomers = customers;
  const filteredBusinesses = businesses.filter((business) =>
    business.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const rootFolders = folders.filter((folder) => isDirectChildFolder(folder.folder, ''));
  const childFolders = selectedFolder
    ? folders.filter((folder) => isDirectChildFolder(folder.folder, selectedFolder.folder))
    : [];
  const sortedChildFolders = [...childFolders].sort((a, b) =>
    getFolderLabel(a.folder).localeCompare(getFolderLabel(b.folder))
  );
  const breadcrumbSegments = selectedFolder
    ? normalizeFolderPath(selectedFolder.folder).split('/').filter(Boolean)
    : [];
  const breadcrumbPaths = breadcrumbSegments.map((_, index) =>
    breadcrumbSegments.slice(0, index + 1).join('/')
  );

  return (
    <Spin spinning={loading || foldersLoading || businessesLoading} size="large">
      <Layout style={{ height: 'calc(100vh - 112px)', background: '#fff', overflow: 'hidden', display: 'flex', margin: '-24px' }}>
      <Sider
        width={300}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          paddingRight: '16px',
          paddingTop: 0,
          paddingLeft: 0,
          paddingBottom: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <div style={{ padding: '16px 16px 0 16px', flexShrink: 0 }}>
        <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>
          Files
        </Title>
        <Radio.Group
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          style={{ marginBottom: 16, width: '100%' }}
        >
          <Radio.Button value="customer" style={{ width: '50%', textAlign: 'center' }}>
            Personal
          </Radio.Button>
          <Radio.Button value="business" style={{ width: '50%', textAlign: 'center' }}>
            Business
          </Radio.Button>
        </Radio.Group>
        <Input
          placeholder={`Search ${entityType === 'customer' ? 'Personal' : 'Business'}`}
          prefix={<SearchOutlined />}
          style={{ marginBottom: 16 }}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          disabled={entityType === 'customer'}
        />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px', minHeight: 0, maxHeight: '70%' }}>
        {entityType === 'customer' ? (
        <List
          loading={false}
          dataSource={filteredCustomers}
            pagination={false}
            style={{ minHeight: 0 }}
          renderItem={(customer) => (
            <List.Item
              style={{
                cursor: 'pointer',
                padding: '12px',
                borderRadius: 4,
                backgroundColor:
                  selectedCustomer?.id === customer.id ? '#e6f7ff' : 'transparent',
                marginBottom: 8,
              }}
              onClick={() => setSelectedCustomer(customer)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{
                      backgroundColor: '#1890ff',
                      color: '#fff',
                    }}
                  >
                    {getCustomerInitials(customer.name)}
                  </Avatar>
                }
                title={customer.name}
              />
            </List.Item>
          )}
        />
        ) : (
          <List
            loading={businessesLoading}
            dataSource={filteredBusinesses}
              pagination={false}
              style={{ minHeight: 0 }}
            renderItem={(business) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: 4,
                  backgroundColor:
                    selectedBusiness?.id === business.id ? '#e6f7ff' : 'transparent',
                  marginBottom: 8,
                }}
                onClick={() => setSelectedBusiness(business)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: '#52c41a',
                        color: '#fff',
                      }}
                    >
                      {getCustomerInitials(business.name)}
                    </Avatar>
                  }
                  title={business.name}
                />
              </List.Item>
            )}
          />
        )}
        </div>
      </Sider>
      <Content 
        style={{ 
          flex: 1,
          overflow: 'hidden',
          background: '#fff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px 24px' }}>
        {selectedFolder ? (
          // Third level: Files view
          <>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={4} style={{ margin: 0 }}>{selectedFolder.customer_name}</Title>
                <Breadcrumb style={{ marginTop: 6 }}>
                  <Breadcrumb.Item>
                    <span
                      style={{ color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedFolder(null);
                        setFiles([]);
                      }}
                    >
                      Folders
                    </span>
                  </Breadcrumb.Item>
                  {breadcrumbPaths.map((path) => (
                    <Breadcrumb.Item key={path}>
                      <span
                        style={{ color: '#1890ff', cursor: 'pointer' }}
                        onClick={() => selectFolderByPath(path)}
                      >
                        {getFolderLabel(path)}
                      </span>
                    </Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              </div>
            </div>
            <Row gutter={[16, 16]}>
              {sortedChildFolders.map((folder) => (
                <Col key={`${folder.customer_id}-${folder.folder}`} xs={24} sm={12} md={8} lg={8}>
                  <Card
                    hoverable
                    onClick={() => selectFolderByPath(folder.folder)}
                    style={{ borderRadius: 8, textAlign: 'center', cursor: 'pointer' }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <FolderOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                    </div>
                    <Title level={5} style={{ marginBottom: 0, marginTop: 0 }}>
                      {getFolderLabel(folder.folder)}
                    </Title>
                  </Card>
                </Col>
              ))}
              {files.map((file) => (
                <Col key={file.id} xs={24} sm={12} md={8} lg={8}>
                  <Card
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
                  >
                    <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                      <div style={{ textAlign: 'center', minWidth: 60 }}>
                        {getFileIcon(file.type)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <Tooltip title={file.name}>
                            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px', maxWidth: '100%' }} ellipsis>
                              {file.name}
                            </Text>
                          </Tooltip>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {file.type.toUpperCase()} • {formatFileSize(file.size)}
                          </Text>
                        </div>
                        <Space style={{ marginTop: 12 }}>
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() => handleView(file)}
                            size="small"
                          >
                            View
                          </Button>
                          <Button
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(file)}
                            size="small"
                          />
                        </Space>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
            {sortedChildFolders.length === 0 && files.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No files found
              </div>
            )}
          </>
        ) : (entityType === 'customer' && selectedCustomer) || (entityType === 'business' && selectedBusiness) ? (
          // Second level: Folders view (cards)
          <>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={3} style={{ margin: 0 }}>
                {entityType === 'customer' ? selectedCustomer?.name : selectedBusiness?.name}
              </Title>
            </div>
            <Row gutter={[16, 16]}>
              {rootFolders.map((folder, index) => {
                // Generate different colors for folder icons
                const colors = ['#1890ff', '#722ed1', '#eb2f96', '#fa8c16', '#52c41a'];
                const color = colors[index % colors.length];
                
                return (
                  <Col key={`${folder.customer_id}-${folder.folder}`} xs={24} sm={12} md={8} lg={8}>
                    <Card
                      hoverable
                      onClick={() => selectFolderByPath(folder.folder)}
                      style={{
                        borderRadius: 8,
                        textAlign: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                      bodyStyle={{ padding: '24px' }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <FolderOutlined
                          style={{
                            fontSize: 64,
                            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            color: color,
                          }}
                        />
                      </div>
                      <Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
                        {getFolderLabel(folder.folder)}
                      </Title>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <FileTextOutlined />
                        <Text type="secondary">
                          {folder.file_count || 0} {folder.file_count === 1 ? 'file' : 'files'}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            {rootFolders.length === 0 && !foldersLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No folders found for this customer
              </div>
            )}
          </>
        ) : (
          // First level: No selection
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {customers.length === 0 ? (
              <div>No customers found</div>
            ) : (
              <div>Select a customer to view folders</div>
            )}
          </div>
        )}
        </div>
      </Content>
      </Layout>
    </Spin>
  );
};
